<template>
  <div class="spond-matches">
    <nav class="navbar navbar-expand-lg navbar-light bg-light border-bottom shadow-sm mb-3">
      <div class="container">
        <router-link class="navbar-brand" to="/">
          <svg width="1.5em" height="1.5em" viewBox="0 0 16 16" class="back-icon bi bi-arrow-left-short" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z"/>
          </svg>
          <span>{{$t('spondMatches.events')}}</span>
        </router-link>           
        <a class="small link-button" v-on:click="signout()">{{$t('spondMatches.logOut')}}</a>
      </div>
    </nav>
    <div class="container">
      <div v-if="!matchesLoaded" class="text-center mt-4">        
        <div class="spinner-border mt-4" style="width: 3rem; height: 3rem;" role="status"></div>
        <div class="text-muted mt-4">{{$t('spondMatches.loading')}}</div>
      </div>
      <div v-if="matchesLoaded && spondGroupName" class="mb-3 d-flex justify-content-between">        
        <h4 class="text-nowrap text-truncate mb-0 pt-1">{{ spondGroupName }}</h4> 
        <router-link to="/spond/groups" class="btn btn-outline-secondary btn-sm text-nowrap">{{$t('spondMatches.changeGroup')}}</router-link>  
      </div>

      <section class="py-5 text-center container" v-if="matchesLoaded && matches && matches.length == 0">
        <div class="row py-lg-5">
          <div class="col-lg-6 col-md-8 mx-auto">
            <h1 class="font-weight-light">{{$t('spondMatches.noEvents')}}</h1>
            
            <p class="lead text-muted">
              {{$t('spondMatches.noEventsDescription')}} <i>{{ spondGroupName }}</i>
            </p>
          </div>
        </div>       
      </section>
      <div v-if="matchesLoaded && matches && matches.length > 0">
        <p class="text-muted mb-4 small">
          {{$t('spondMatches.description')}}
        </p>
        <div v-for="match in matches" :key="match.id">
          <div class="card mb-3 shadow-sm">
            <div class="card-body pb-2">
              <h5 class="card-title">{{ match.heading }}</h5>
              <p class="card-text pb-0 text-muted d-flex justify-content-between">
                <span>{{ match.startTimestampLocale }}</span>
                <a class="card-link small  link-button" v-on:click="createFromSpondMatch(match)">{{$t('spondMatches.create')}}</a>
              </p>
            </div>
          </div>          
        </div>
      </div>
    </div>    
  </div>
</template>

<script lang="ts">
import { Vue } from "vue-class-component";
import moment from "moment";
import { set, get, keys, del, Store } from "idb-keyval";
import { Guid } from "guid-typescript";
import { SpondMatch, Match, Player } from "../models/Models";
import { SpondService } from '../services/SpondService';
import { inject } from 'vue';
import router from '../router';

export default class SpondMatches extends Vue {
  public matches: Array<SpondMatch> = [];
  public matchesLoaded = false;
  private spondService: SpondService;

  public spondGroupName = '';

  async created() {
    this.spondService = inject('SpondService', new SpondService());

    this.spondService.getMatches()
      .then(async matches => {
        this.matches = matches;
        this.matchesLoaded = true;

        this.spondGroupName = await this.spondService.getSelectedGroupName();
      })
      .catch(async err => {
        if (err === 'no_group') {
          router.push({ name: "SpondGroups" });   
          return;
        } else if (err === 'no_token') {
          router.push({ name: "SpondLogin" });   
          return;
        } else {
          router.push({ name: "SpondLogin" });   
          return;
        }
      });
  }

  async createFromSpondMatch(spondMatch: SpondMatch) {
    if (!confirm(this.$t('spondMatches.confirmCreate') + ' ' + spondMatch.heading)) {
      return;
    }

    const match: Match = {
      id: spondMatch.id,
      homeCount: 0,
      opponent: spondMatch.heading,
      awayCount: 0,
      isRunning: false,
      hasStarted: false,
      totalSecondsPlayed: 0,
      homeGame: undefined,
      matchPlayedTime: "",
      lastStarted: undefined,
      players: [],
      created: moment().format('YYYY-MM-DD HH:mm:ss')
    };

    spondMatch.players.forEach(p => {
      const newPlayer: Player = {
        id: p.id,
        key: p.id,
        name: p.shortName,
        lastSecondsOnField: 0,
        lastSecondsOfField: 0,
        totalSecondsOnField: 0,
        lastOnField: undefined,
        lastOfField: undefined,
        isOnField: false,
        lastStartOnField: undefined,
        _editing: false,
        _editingName: undefined,
        _timeOnField: "00:00",
        _timeOffField: "00:00",
        _timeTotal: "00:00"
      };

      match.players.push(newPlayer);
    });

    await set(match.id, match);

    router.push({ name: "MatchDetails", params: { id: match.id } });    
  }

  goToMatch(match: SpondMatch) {   
    router.push({ name: "MatchDetails", params: { id: match.id } });    
  }
  
  async signout() {
    await this.spondService.signout();
    
    router.push({ name: "Matches" });  
  }
}
</script>
